import React, { useCallback, useState } from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface IntegerInputProps extends BaseInputProps {
  /** Valor numérico entero o null */
  value: number | null;
  /** Callback cuando cambia el valor */
  onChange: (value: number | null) => void;
  /** Valor mínimo permitido */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Permitir números negativos (default: false) */
  allowNegative?: boolean;
  /** Nombre del campo para formularios */
  name?: string;
}

/**
 * Características:
 * - Procesa input como string internamente
 * - Valida que sea número entero
 * - Devuelve number | null en onChange
 * - Soporta min/max y números negativos opcionales
 */
export const IntegerInput: React.FC<IntegerInputProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  min,
  max,
  allowNegative = false,
  name,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(
    value !== null ? String(value) : '',
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Permitir string vacío
      if (rawValue === '') {
        setDisplayValue('');
        onChange(null);
        return;
      }

      // Permitir solo signo negativo al inicio si allowNegative
      if (allowNegative && rawValue === '-') {
        setDisplayValue('-');
        return;
      }

      // Validar que sea un número entero válido
      const regex = allowNegative ? /^-?\d*$/ : /^\d*$/;
      if (!regex.test(rawValue)) {
        return;
      }

      const numValue = parseInt(rawValue, 10);

      // Validar rango
      if (!isNaN(numValue)) {
        if (min !== undefined && numValue < min) {
          return;
        }
        if (max !== undefined && numValue > max) {
          return;
        }
      }

      setDisplayValue(rawValue);
      onChange(isNaN(numValue) ? null : numValue);
    },
    [onChange, allowNegative, min, max],
  );

  // Sincronizar con valor externo
  React.useEffect(() => {
    const newDisplayValue = value !== null ? String(value) : '';
    if (displayValue !== newDisplayValue && displayValue !== '-') {
      setDisplayValue(newDisplayValue);
    }
  }, [value, displayValue]);

  return (
    <StyledTextField
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      label={label}
      disabled={disabled}
      error={error}
      helperText={helperText}
      required={required}
      fullWidth={fullWidth}
      placeholder={placeholder}
      name={name}
    />
  );
};
